import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import { ListTodo, Plus, Play, Check, X } from "lucide-react";
import EntityLink from "../components/EntityLink";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useScopedModuleId } from "../contexts/ModuleScopeContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import EmptyState from "../components/EmptyState";
import { Skeleton } from "../components/ui/skeleton";
import { usePageAction } from "../contexts/UXChromeContext";
import { toast } from "sonner";
import RowActions from "../components/RowActions";
import ShowDeletedToggle, { DeletedRowDecorator, DeletedPill } from "../components/ShowDeletedToggle";
import { Checkbox } from "../components/ui/checkbox";
import useBulkSelection from "../hooks/useBulkSelection";
import BulkActionsBar from "../components/BulkActionsBar";
import { useHasPermission } from "../contexts/PermissionsContext";
import { rowOpenProps } from "../hooks/useRowOpen";
import DetailDialog from "../components/DetailDialog";
import DateInput from "../components/DateInput";

const COLUMNS = [
    { key: "pending", label: "Pending", tone: "bg-amber-50" },
    { key: "in_progress", label: "In Progress", tone: "bg-blue-50" },
    { key: "done", label: "Done", tone: "bg-emerald-50" },
    { key: "cancelled", label: "Cancelled", tone: "bg-gray-100" },
];

const PRIORITY_TONE = {
    low: "bg-gray-100 text-gray-700",
    medium: "bg-blue-50 text-blue-700",
    high: "bg-amber-50 text-amber-700",
    urgent: "bg-rose-50 text-rose-700",
};

export default function Tasks() {
    const navigate = useNavigate();
    const { moduleCode: pathModuleCode } = useParams();
    const { user } = useAuth();
    const scopedId = useScopedModuleId();
    const canCreate = useHasPermission("tasks.write", scopedId || null);
    const canCancelTask = useHasPermission("tasks.cancel", scopedId || null);
    // Iter60.p50 — `tasks.complete` STRIPPED from Attendance Officer.
    // Guard the Complete action separately so create/edit stay visible.
    const canCompleteTask = useHasPermission("tasks.complete", scopedId || null);
    const [detail, setDetail] = useState(null);

    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);

    usePageAction(canCreate ? { label: "New Task", run: () => setOpen(true) } : null, [canCreate]);

    const load = async () => {
        setLoading(true);
        try {
            const params = { page_size: 200 };
            if (scopedId) params.module_id = scopedId;
            if (showDeleted) params.include_deleted = true;
            const [r, e] = await Promise.all([
                api.get("/tasks", { params }),
                api.get("/employees", { params: { module_id: scopedId, page_size: 200 } }),
            ]);
            setItems(r.data.items || []);
            setEmployees(e.data.items || e.data || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [scopedId, showDeleted]);

    const empMap = useMemo(() => Object.fromEntries(employees.map(e => [e.id, e])), [employees]);
    const grouped = useMemo(() => {
        const out = { pending: [], in_progress: [], done: [], cancelled: [] };
        items.forEach(t => out[t.status]?.push(t));
        return out;
    }, [items]);

    const action = async (id, path, body) => {
        try {
            await api.post(`/tasks/${id}/${path}`, body || {});
            toast.success("Done");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    // Iter56 → Iter56c — bulk selection across all task cards on the board.
    // MUST be declared before the early return so hook order stays stable.
    // Uses module-scoped permission checks to match individual row buttons.
    const canBulkWrite = useHasPermission("tasks.write", scopedId || null);
    const canBulkDelete = useHasPermission("tasks.delete", scopedId || null);
    const bulk = useBulkSelection(items, (t) => t.id);

    if (loading) {
        return (
            <section className="space-y-4" data-testid="tasks-page">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="tasks-loading">
                    {COLUMNS.map(c => (
                        <div key={c.key} className="asl-card p-3 space-y-2">
                            <Skeleton className="h-3 w-20" />
                            {[0, 1].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    const isEmpty = items.length === 0;

    return (
        <section className="space-y-4" data-testid="tasks-page">
            <div className="flex items-center justify-between gap-3">
                <ShowDeletedToggle value={showDeleted} onChange={setShowDeleted} testid="tasks-show-deleted" />
                {canCreate && (
                    <Button onClick={() => setOpen(true)} className="asl-navy-bg text-white" data-testid="tasks-new-btn">
                        <Plus className="h-4 w-4 mr-1" /> New Task
                    </Button>
                )}
            </div>
            {!isEmpty && (canBulkWrite || canBulkDelete) && (
                <BulkActionsBar
                    resource="tasks"
                    selectedIds={bulk.selectedIds}
                    onClear={bulk.clear}
                    onRefresh={load}
                    entityLabel="task"
                    canApprove={canBulkWrite}
                    approveLabel="Mark done"
                    approveVerb="marked done"
                />
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="tasks-board">
                {COLUMNS.map(col => (
                    <div key={col.key} className="asl-card overflow-hidden flex flex-col">
                        <div className={`px-3 py-2 text-xs uppercase font-bold ${col.tone}`} data-testid={`tasks-col-${col.key}`}>
                            {col.label} <span className="ml-1 text-[10px] text-[color:var(--asl-muted)] mono">{grouped[col.key].length}</span>
                        </div>
                        <div className="p-2 space-y-2 flex-1 min-h-[120px]">
                            {grouped[col.key].length === 0 && (
                                <div className="text-[11px] text-[color:var(--asl-muted)] italic px-1 py-2">No tasks</div>
                            )}
                            {grouped[col.key].map(t => (
                                <div key={t.id} {...rowOpenProps(navigate, () => setDetail(t), { testid: `task-card-${t.id}`, className: "bg-white rounded border p-2.5 space-y-1.5" })} style={{ borderColor: "var(--asl-border)" }}>
                                    <div className="flex items-start justify-between gap-2">
                                        {(canBulkWrite || canBulkDelete) && (
                                            <span onClick={(e) => e.stopPropagation()} className="pt-0.5">
                                                <Checkbox
                                                    checked={bulk.isSelected(t.id)}
                                                    onCheckedChange={() => bulk.toggle(t.id)}
                                                    data-testid={`bulk-select-${t.id}`}
                                                />
                                            </span>
                                        )}
                                        <div className="text-sm font-semibold asl-navy-text leading-tight flex-1">
                                            <DeletedRowDecorator deleted={!!t.deleted_at}>{t.title}</DeletedRowDecorator>
                                            {t.deleted_at && <DeletedPill deletedAt={t.deleted_at} />}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span className={`text-[9px] uppercase font-bold rounded-full px-1.5 py-0.5 ${PRIORITY_TONE[t.priority]}`}>{t.priority}</span>
                                            <RowActions
                                                entity="tasks"
                                                endpoint="/tasks"
                                                id={t.id}
                                                label={t.title}
                                                deleted={!!t.deleted_at}
                                                moduleId={t.module_id}
                                                onChange={load}
                                                testid={`task-actions-${t.id}`}
                                            />
                                        </div>
                                    </div>
                                    <div className="text-[11px] text-[color:var(--asl-muted)]">
                                        {t.assigned_employee_id ? (
                                            <EntityLink to={`/m/${pathModuleCode}/staff/${t.assigned_employee_id}`} testid={`task-emp-link-${t.id}`}>
                                                {empMap[t.assigned_employee_id]?.full_name || "—"}
                                            </EntityLink>
                                        ) : "—"}
                                    </div>
                                    {t.due_date && <div className="text-[10px] mono text-[color:var(--asl-muted)]">due {t.due_date}</div>}
                                    <div className="flex gap-1 pt-1">
                                        {t.status === "pending" && (
                                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => action(t.id, "start")} data-testid={`task-start-${t.id}`}>
                                                <Play className="h-3 w-3 mr-0.5" /> Start
                                            </Button>
                                        )}
                                        {["pending", "in_progress"].includes(t.status) && canCompleteTask && (
                                            <Button size="sm" className="h-6 text-[10px] px-2 asl-navy-bg text-white" onClick={() => action(t.id, "complete", { evidence_url: null })} data-testid={`task-complete-${t.id}`}>
                                                <Check className="h-3 w-3 mr-0.5" /> Done
                                            </Button>
                                        )}
                                        {!["done", "cancelled"].includes(t.status) && canCancelTask && (
                                            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={() => action(t.id, "cancel")} data-testid={`task-cancel-${t.id}`}>
                                                <X className="h-3 w-3" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            {isEmpty && (
                <div className="asl-card overflow-hidden">
                    <EmptyState
                        icon={ListTodo}
                        title="No tasks yet"
                        description="Assign a task to an employee — track it through pending → in-progress → done."
                        ctaLabel={canCreate ? "New Task" : undefined}
                        onCta={canCreate ? () => setOpen(true) : undefined}
                        testid="tasks-empty"
                    />
                </div>
            )}
            <NewTaskDialog open={open} onOpenChange={setOpen} onSaved={load} employees={employees} />
            <DetailDialog
                row={detail}
                onClose={() => setDetail(null)}
                title="Task"
                testid="task-detail"
                moduleCodeFallback={pathModuleCode}
                actions={detail ? [
                    { label: "Start", visible: detail.status === "pending" && canCreate, onClick: async () => { await action(detail.id, "start"); setDetail(null); }, testid: "task-detail-start", className: "asl-navy-bg text-white" },
                    { label: "Complete", visible: detail.status === "in_progress" && canCompleteTask, onClick: async () => { await action(detail.id, "complete", { evidence_url: null }); setDetail(null); }, testid: "task-detail-complete", className: "asl-navy-bg text-white" },
                    { label: "Cancel Task", visible: !["done", "cancelled"].includes(detail.status) && canCancelTask, onClick: async () => { await action(detail.id, "cancel"); setDetail(null); }, testid: "task-detail-cancel" },
                ] : []}
            />
        </section>
    );
}

function NewTaskDialog({ open, onOpenChange, onSaved, employees }) {
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({
        title: "", description: "", assigned_employee_id: "", priority: "medium", due_date: today, notes: "",
    });
    useEffect(() => { if (open) setForm({ title: "", description: "", assigned_employee_id: "", priority: "medium", due_date: today, notes: "" }); }, [open, today]);
    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/tasks", form);
            toast.success("Task created");
            onOpenChange(false);
            await onSaved();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="tasks-new-dialog">
                <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3 pt-1">
                    <div><Label className="text-xs uppercase">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
                    <div><Label className="text-xs uppercase">Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase">Assignee</Label>
                            <Select value={form.assigned_employee_id} onValueChange={(v) => setForm({ ...form, assigned_employee_id: v })}>
                                <SelectTrigger data-testid="tasks-emp-select"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name} ({e.employee_code})</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase">Priority</Label>
                            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Low</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="high">High</SelectItem>
                                    <SelectItem value="urgent">Urgent</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div><Label className="text-xs uppercase">Due date</Label><DateInput value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} /></div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button type="submit" className="asl-navy-bg text-white" data-testid="tasks-submit">Create</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
